import React, { useEffect, useRef, useState } from 'react';
import { Snackbar, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from '@mui/styles';

import { closeSnackbar } from '../../../store/actions/ui';
import { snackbarTypes } from './config';
import { colors } from '../../../utils/colors';
import { useLocalizationContext } from '../../../localization/LocalizationContext';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';

const useStyles = makeStyles({
    close: {
        padding: '2px'
    }
});

const snackbarColorMapping: Record<string, string> = {
    [snackbarTypes.info]: colors.blue.i2x_flashy,
    [snackbarTypes.success]: colors.green.jade_2,
    [snackbarTypes.warning]: colors.yellow.neon_carrot,
    [snackbarTypes.error]: colors.red.rose
};

interface SnackbarStateSnapshot {
    isOpen: boolean;
    messageKey: string | null;
    messageType: string;
}

export const SimpleSnackbar: React.FC = () => {
    const classes = useStyles();
    const localization = useLocalizationContext();
    const dispatch = useAppDispatch();

    const isOpen = useAppSelector(state => state.ui?.snackbar?.isOpen ?? false);
    const messageKey = useAppSelector(state => state.ui?.snackbar?.messageKey ?? null);
    const messageType = useAppSelector(state => state.ui?.snackbar?.messageType ?? snackbarTypes.info);

    // There was a flash when closing the snackbar notification; keep previous message/color while closing.
    const [currentProps, setCurrentProps] = useState<SnackbarStateSnapshot>({
        isOpen: false,
        messageKey: null,
        messageType: snackbarTypes.info
    });

    useEffect(() => {
        if (isOpen) {
            setCurrentProps({ isOpen, messageKey, messageType });
        }
    }, [isOpen, messageKey, messageType]);

    const effectiveMessageKey = (isOpen ? messageKey : currentProps.messageKey) ?? undefined;
    const effectiveMessageType = (isOpen ? messageType : currentProps.messageType) ?? snackbarTypes.info;

    const messageText = localization.getText(effectiveMessageKey);
    const backgroundColor = snackbarColorMapping[effectiveMessageType];

    useEffect(() => {
        if (isOpen) {
            setCurrentProps({ isOpen, messageKey, messageType });
        }

        return () => {
            setCurrentProps({
                isOpen: false,
                messageKey: null,
                messageType: snackbarTypes.info
            });
        };
    }, [isOpen, messageKey, messageType]);

    const handleSnackbarClose = (
        _event?: React.SyntheticEvent | Event,
        _reason?: 'timeout' | 'clickaway' | 'escapeKeyDown'
    ) => {
        dispatch(closeSnackbar());
    };

    return (
        <Snackbar
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
            }}
            open={Boolean(isOpen && messageText && messageText.length > 0)}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            slotProps={{
                content: {
                    'aria-describedby': 'message-id',
                    style: { backgroundColor }
                }
            }}
            message={
                <span data-test="notification" data-test-value={effectiveMessageType} id="message-id">
                    {messageText}
                </span>
            }
            action={
                <IconButton
                    key="close"
                    aria-label="Close"
                    color="inherit"
                    className={classes.close}
                    onClick={() => handleSnackbarClose()}>
                    <CloseIcon />
                </IconButton>
            }
        />
    );
};
